import React, { useCallback } from 'react';
import { css } from '@emotion/css';
import { Modal } from 'is24-corecss';
import ModalV2 from '../../../../common/display/modal/ModalV2';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { ProductType } from '../../../../../@types/product';
import MieterNetzwerkPng from './images/mieter-netzwerk.png';
import MieterNetzwerkWebp from './images/mieter-netzwerk.webp';
import MieterNetzwerkMobilePng from './images/mieter-netzwerk-mobile.png';
import MieterNetzwerkMobileWebp from './images/mieter-netzwerk-mobile.webp';
import { LISTING_STATE } from './mieterNetzwerk.utils';
import { useTenantNetworkController } from './useMieterNetzwerk.hook';
import { report } from '../../../../../tracking/tracking';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { campaigns } from '../../../../common/campaigns';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';
import { useGetHasProduct } from '@/customHooks/useGetHasProduct.hook';

const styles = {
  overviewTab: css`
    max-width: 530px;
  `,
};

type CtaTranslation = { id: string; defaultMessage: string };
type CtaConfig = { primary: CtaTranslation; secondary: CtaTranslation | null };

const CTA_CONFIG: Record<string, CtaConfig> = {
  basic: {
    primary: { id: 'benefits.mieterNetzwerk.modal.cta.discoverSuchenPlus', defaultMessage: 'Suchen+ entdecken' },
    secondary: null,
  },
  [LISTING_STATE.ELIGIBLE]: {
    primary: { id: 'benefits.mieterNetzwerk.modal.cta.ELIGIBLE', defaultMessage: 'Mieter-Netzwerk beitreten' },
    secondary: null,
  },
  [LISTING_STATE.DRAFT]: {
    primary: { id: 'benefits.mieterNetzwerk.modal.cta.DRAFT', defaultMessage: 'Mieter-Netzwerk beitreten' },
    secondary: null,
  },
  [LISTING_STATE.PUBLISHED]: {
    primary: { id: 'benefits.mieterNetzwerk.modal.cta.editImmos', defaultMessage: "Bearbeiten unter 'Meine Immos'" },
    secondary: { id: 'benefits.mieterNetzwerk.modal.cta.startSearch', defaultMessage: 'Jetzt mit der Suche starten' },
  },
};

const OverviewTab = ({ isEntitledPlusRent }: { isEntitledPlusRent: boolean }) => (
  <div className={styles.overviewTab}>
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <Msg
        id='benefits.mieterNetzwerk.modal.overview.paragraph1'
        defaultMessage='Entdecke unveröffentlichte Wohnungsangebote im Mieter-Netzwerk.'
      />
    </div>
    <div className='padding-bottom-l'>
      <Msg
        id='benefits.mieterNetzwerk.modal.overview.paragraph2'
        defaultMessage='Werde Teil des Mieter-Netzwerks und erfahre wer gerade auch nach einer neuen Bleibe sucht und welche Wohnungen bald verfügbar werden.'
      />
    </div>
    {!isEntitledPlusRent && (
      <ul className='list-check list-spacing-s'>
        <li data-testid='mieter-plus-label-with-checkmark'>
          <Msg
            id='benefits.mieterNetzwerk.modal.activeMieterPlus'
            defaultMessage='In Suchen+ für Miete enthalten'
            values={{ Logo: <PlusBadge variant='search' /> }}
          />
        </li>
      </ul>
    )}
  </div>
);

/* ======================================================
   Main Component
====================================================== */

export const MieterNetzwerk = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);

  const {
    initializing,
    listingState,
    isOnboardingActive,
    isWidgetLoading,
    handleOpenMeinImmobilien,
    handleOpenHomepage,
    handleStartOnboarding,
  } = useTenantNetworkController(isVisible, onClose);

  const ctaConfig = !isEntitledPlusRent ? CTA_CONFIG.basic : CTA_CONFIG[listingState];
  const primaryLabel = ctaConfig?.primary ? t(ctaConfig.primary) : null;
  const secondaryLabel = ctaConfig?.secondary ? t(ctaConfig.secondary) : null;

  const handleCtaClick = useCallback(async () => {
    if (!isEntitledPlusRent) {
      window.location.href = campaigns.TENANT_NETWORK_SUCHEN_PLUS;
      return;
    }
    await report(OverviewTrackingEvents.MIETER_NETZWERK.FUNNEL_SUBMIT, isEntitledPlusRent);
    if (listingState === LISTING_STATE.PUBLISHED) {
      handleOpenMeinImmobilien();
    } else {
      handleStartOnboarding();
    }
  }, [isEntitledPlusRent, listingState, handleOpenMeinImmobilien, handleStartOnboarding]);

  return (
    <ModalV2
      title={t({ id: 'benefits.mieterNetzwerk.modal.headline', defaultMessage: 'Mieter-Netzwerk' })}
      isVisible={isVisible && !isOnboardingActive}
      image={MieterNetzwerkPng}
      imageWebp={MieterNetzwerkWebp}
      imageMobile={MieterNetzwerkMobilePng}
      imageMobileWebp={MieterNetzwerkMobileWebp}
      imageAlt={t({ id: 'benefits.mieterNetzwerk.modal.image', defaultMessage: 'Mieter-Netzwerk' })}
      imageUseFullWidth={true}
      tabsContent={[<OverviewTab key='overview' isEntitledPlusRent={isEntitledPlusRent} />]}
      {...(primaryLabel && {
        primaryFooterBtn: (
          <Modal.PrimaryFooterButton
            data-testid='mieter-netzwerk-primary-cta'
            label={primaryLabel}
            onClick={handleCtaClick}
            {...((initializing || isWidgetLoading) && { loading: true })}
          />
        ),
      })}
      {...(secondaryLabel && {
        secondaryFooterBtn: (
          <Modal.SecondaryFooterButton
            data-testid='mieter-netzwerk-secondary-cta'
            label={secondaryLabel}
            onClick={handleOpenHomepage}
          />
        ),
      })}
      benefit={Benefit.MIETER_NETZWERK}
      onClose={onClose}
    />
  );
};
